import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { AlertTriangle, Check, Download, Loader2, RefreshCw } from 'lucide-react';
import { supabase } from '@/lib/supabase';

/** One journey a bus already makes that has no fare, plus a suggested price. */
interface FareGap {
  corridor_id: string;
  corridor_name: string;
  origin: string;
  destination: string;
  stop_span: number;
  distance_km: number;
  suggested_fare_xcd: number | null;
  suggestion_basis: string;
}

interface FareGapsProps {
  onRefresh: () => void;
}

/** Fares move in 50c steps, so snap typed values the same way suggestions are. */
function isValidFare(raw: string): boolean {
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 && n < 200;
}

/**
 * Fare Gaps — journeys a single bus serves that the app has to refuse.
 *
 * Stops carry a position on each corridor, so two stops on the same corridor
 * in order are both passed by one bus. When no fare row covers that pair the
 * rider is told "no direct bus" and offered a two-leg trip instead — which
 * for Jimmit to Mero meant riding south to Roseau and back north past their
 * own village for EC$8.00, on a route one bus covers.
 *
 * This screen lists those pairs with a suggested price derived from what the
 * same corridor already charges for a hop of the same length. The operator
 * confirms or corrects each one; nothing is written without a click, and
 * accepted fares land as drafts so they can be reviewed before going live.
 */
export default function FareGaps({ onRefresh }: FareGapsProps) {
  const [gaps, setGaps] = useState<FareGap[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [corridorFilter, setCorridorFilter] = useState<string>('all');
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
  const [publishing, setPublishing] = useState(false);

  const keyFor = (g: FareGap) => `${g.origin}→${g.destination}`;

  const fetchGaps = useCallback(async () => {
    try {
      const { data, error: rpcError } = await supabase.rpc('admin_missing_corridor_fares');
      if (rpcError) throw rpcError;
      setError(null);
      setGaps(Array.isArray(data) ? (data as FareGap[]) : []);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : JSON.stringify(err);
      console.error('[fare-gaps]', err);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchGaps(); }, [fetchGaps]);

  const corridorNames = useMemo(() => {
    const seen = new Map<string, string>();
    gaps.forEach(g => seen.set(g.corridor_id, g.corridor_name));
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [gaps]);

  const visible = useMemo(() => {
    const rows = corridorFilter === 'all'
      ? gaps
      : gaps.filter(g => g.corridor_id === corridorFilter);
    return [...rows].sort((a, b) =>
      a.corridor_name.localeCompare(b.corridor_name)
      || a.stop_span - b.stop_span
      || a.origin.localeCompare(b.origin));
  }, [gaps, corridorFilter]);

  const valueFor = (g: FareGap) =>
    edits[keyFor(g)] ?? (g.suggested_fare_xcd != null ? String(g.suggested_fare_xcd) : '');

  /** Write one accepted fare as a draft. */
  const acceptGap = async (g: FareGap, raw: string): Promise<boolean> => {
    if (!isValidFare(raw)) {
      toast.error(`Enter a fare for ${g.origin} → ${g.destination}`);
      return false;
    }
    const { data, error: rpcError } = await supabase.rpc('admin_upsert_fare', {
      p_id: null,
      p_origin: g.origin,
      p_destination: g.destination,
      p_fare_xcd: Number(raw),
      p_notes: `Filled from fare gaps review · ${g.suggestion_basis}`,
      p_is_draft: true,
    });
    if (rpcError) {
      toast.error(rpcError.message);
      return false;
    }
    const result = data as any;
    if (result?.ok === false) {
      toast.error(result.error || 'Failed to save fare');
      return false;
    }
    return true;
  };

  const handleAcceptOne = async (g: FareGap) => {
    const key = keyFor(g);
    setSavingKey(key);
    try {
      const ok = await acceptGap(g, valueFor(g));
      if (!ok) return;
      toast.success(`${g.origin} → ${g.destination} set to EC$${Number(valueFor(g)).toFixed(2)} (draft)`);
      // Drop it locally so the list shrinks immediately, then resync.
      setGaps(prev => prev.filter(x => keyFor(x) !== key));
      onRefresh();
    } finally {
      setSavingKey(null);
    }
  };

  /**
   * Accept everything currently shown. Runs in small batches so a few
   * hundred rows do not fire hundreds of parallel requests, and reports
   * partial success honestly rather than claiming a clean sweep.
   */
  const handleAcceptAll = async () => {
    const rows = visible.filter(g => isValidFare(valueFor(g)));
    if (rows.length === 0) {
      toast.error('Nothing to accept. There are no valid fares in view.');
      return;
    }
    setBulkOpen(false);
    setBulkProgress({ done: 0, total: rows.length });
    let saved = 0;
    const failed: string[] = [];
    const BATCH = 8;
    for (let i = 0; i < rows.length; i += BATCH) {
      const batch = rows.slice(i, i + BATCH);
      const results = await Promise.all(
        batch.map(async g => ({ g, ok: await acceptGap(g, valueFor(g)) })),
      );
      results.forEach(({ g, ok }) => {
        if (ok) saved += 1;
        else failed.push(`${g.origin} → ${g.destination}`);
      });
      setBulkProgress({ done: Math.min(i + BATCH, rows.length), total: rows.length });
    }
    setBulkProgress(null);
    if (saved > 0) {
      toast.success(`${saved} fare${saved > 1 ? 's' : ''} saved as draft. Review, then Publish.`);
    }
    if (failed.length > 0) {
      toast.error(`${failed.length} could not be saved (first: ${failed[0]})`);
    }
    await fetchGaps();
    onRefresh();
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const { data, error: rpcError } = await supabase.rpc('admin_publish_drafts', {
        p_corridor_id: null,
      });
      if (rpcError) throw rpcError;
      const result = data as any;
      if (result?.ok === false) throw new Error(result.error || 'Publish failed');
      const published = Number(result?.fares_published ?? 0);
      if (published > 0) {
        toast.success(`${published} fare${published > 1 ? 's' : ''} published. Riders see them within seconds.`);
      } else {
        toast.error('No fares were published. Reload and try again, or check your admin access.');
      }
      await fetchGaps();
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || 'Failed to publish fares');
    } finally {
      setPublishing(false);
    }
  };

  /** Export for pricing offline or checking against the official schedule. */
  const handleExport = () => {
    const header = 'Corridor,Origin,Destination,Stops apart,Distance km,Suggested XCD,Basis';
    const lines = visible.map(g => [
      g.corridor_name, g.origin, g.destination, g.stop_span, g.distance_km,
      g.suggested_fare_xcd ?? '', `"${g.suggestion_basis.replace(/"/g, '""')}"`,
    ].join(','));
    const blob = new Blob([[header, ...lines].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'h-plate-missing-fares.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center p-8">
        <div className="max-w-lg text-center space-y-4">
          <h2 className="text-xl font-semibold text-white">Could not load fare gaps</h2>
          <p className="text-gray-400 text-sm font-mono bg-gray-800/50 rounded-lg p-4">{error}</p>
          <Button onClick={() => { setLoading(true); fetchGaps(); }}>Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Explanation — an operator should not need to be told what this is */}
      <div className="px-4 pt-4">
        <div className="rounded-lg border border-amber-600/30 bg-amber-600/10 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-100/90 space-y-1">
              <p className="font-semibold text-amber-200">
                {gaps.length} journeys a bus already makes have no fare
              </p>
              <p>
                Both stops sit on the same corridor in order, so one bus passes
                both. With no fare, the app tells riders <em>"no direct bus"</em> and
                sends them on a two-leg trip, sometimes backwards past their own
                village.
              </p>
              <p className="text-amber-200/70">
                Suggested prices are estimated from what this corridor already
                charges for a hop of the same length. They are a starting point,
                not official. Check each one before publishing.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="px-4 py-3 flex items-center gap-2 flex-wrap">
        <Select value={corridorFilter} onValueChange={setCorridorFilter}>
          <SelectTrigger className="w-[220px]">
            <SelectValue placeholder="All corridors" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All corridors ({gaps.length})</SelectItem>
            {corridorNames.map(([id, name]) => (
              <SelectItem key={id} value={id}>
                {name} ({gaps.filter(g => g.corridor_id === id).length})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button variant="outline" size="sm" onClick={() => { setLoading(true); fetchGaps(); }}>
          <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>

        <Button variant="outline" size="sm" onClick={handleExport}>
          <Download className="w-4 h-4 mr-1.5" /> Export CSV
        </Button>

        <div className="flex-1" />

        <Button
          variant="outline"
          size="sm"
          onClick={() => setBulkOpen(true)}
          disabled={visible.length === 0 || bulkProgress !== null}
        >
          Accept all {visible.length} shown
        </Button>

        <Button size="sm" onClick={handlePublish} disabled={publishing}>
          {publishing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
          Publish drafts
        </Button>
      </div>

      {bulkProgress && (
        <div className="px-4 pb-2 text-sm text-gray-400 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          Saving {bulkProgress.done} of {bulkProgress.total}…
        </div>
      )}

      {/* Worklist */}
      <div className="flex-1 overflow-auto px-4 pb-4">
        {visible.length === 0 ? (
          <div className="h-full flex items-center justify-center text-center">
            <div className="space-y-2">
              <Check className="w-10 h-10 text-emerald-500 mx-auto" />
              <p className="text-white font-medium">No gaps here</p>
              <p className="text-gray-400 text-sm">
                Every pair on this corridor that one bus serves has a fare.
              </p>
            </div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-900">
              <tr className="text-left text-gray-400 border-b border-gray-800">
                <th className="py-2 pr-3 font-medium">Journey</th>
                <th className="py-2 pr-3 font-medium">Corridor</th>
                <th className="py-2 pr-3 font-medium whitespace-nowrap">Stops apart</th>
                <th className="py-2 pr-3 font-medium">Distance</th>
                <th className="py-2 pr-3 font-medium">Fare (EC$)</th>
                <th className="py-2 pr-3 font-medium">Where the suggestion came from</th>
                <th className="py-2 font-medium" />
              </tr>
            </thead>
            <tbody>
              {visible.map(g => {
                const key = keyFor(g);
                const busy = savingKey === key;
                return (
                  <tr key={key} className="border-b border-gray-800/60 hover:bg-gray-800/30">
                    <td className="py-2 pr-3 text-white whitespace-nowrap">
                      {g.origin} <span className="text-gray-500">→</span> {g.destination}
                    </td>
                    <td className="py-2 pr-3">
                      <Badge className="bg-gray-700/40 text-gray-300 border-gray-600/40 text-[10px]">
                        {g.corridor_name}
                      </Badge>
                    </td>
                    <td className="py-2 pr-3 text-gray-400">{g.stop_span}</td>
                    <td className="py-2 pr-3 text-gray-400 whitespace-nowrap">
                      {Number(g.distance_km).toFixed(1)} km
                    </td>
                    <td className="py-2 pr-3">
                      <Input
                        value={valueFor(g)}
                        onChange={e => setEdits(prev => ({ ...prev, [key]: e.target.value }))}
                        inputMode="decimal"
                        className="w-24 h-8"
                        aria-label={`Fare for ${g.origin} to ${g.destination}`}
                      />
                    </td>
                    <td className="py-2 pr-3 text-gray-500 text-xs max-w-[280px]">
                      {g.suggestion_basis}
                    </td>
                    <td className="py-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleAcceptOne(g)}
                        disabled={busy || bulkProgress !== null}
                      >
                        {busy
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <><Check className="w-4 h-4 mr-1" /> Accept</>}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Accept {visible.length} suggested fares?</DialogTitle>
            <DialogDescription className="space-y-2">
              <span className="block">
                This saves them all as <strong>drafts</strong>. Riders will not see
                them until you press Publish, so you can still review or change
                any of them in the Fare Matrix first.
              </span>
              <span className="block text-amber-300/80">
                These are estimates based on similar journeys, not official
                government fares.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button onClick={handleAcceptAll}>Save {visible.length} as drafts</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
